import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Use private libraries to cleanup your codebase",
  "description": "You're using the many-small-repositories approach to work faster as a team. But where do you put shared code? How do you make it nice to use? How to publish and install private packages?",
  "categories": "Technical, Git, NPM, packages",
  "published": "2020-11-16T00:00:00.000Z",
  "hero": "./img/screenshot-1605543263325.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Picture this: You're using the many-small-repositories approach to work faster as a team.`}</p>
    <p>{`You've got an admin repo, a backend repo, a mobile repo, a repo for the corp site, another for the main web app, a few experiments that died in A/B test, and let's be honest there's multiple backend repos. One per service. Web app likes to grow into multiple repos, too. One per user concern.`}</p>
    <p>{`You get more done faster and each codebase makes sense on its own. You can even deploy separately 🤘`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media2.giphy.com/media/3o7buiQeyYFamzRoR2/giphy-loop.mp4?cid=7cf28b0fqxft1mzsuuvbczvch3ejwbe2g4w7npg5fli9y1ty&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`And then your plan falls apart 👉 shared code.`}</p>
    <p>{`The first candidate is your design system. After that come utility functions. Then user management and shared API queries.`}</p>
    <p>{`Best solution I've found for those are internal libraries. Libraries built for you and your team. Not meant to be shared.`}</p>
    <p>{`If they prove out, you can open-source later. Like Facebook did with React. But that's a lot of work, trust me.`}</p>
    <h2 {...{
      "id": "how-to-create-a-javascript-library-or-package"
    }}>{`How to create a JavaScript library or package`}</h2>
    <p>{`Don't overthink this one. A JavaScript library is a bundle of code with a `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file and a name.`}</p>
    <p>{`Easiest way to get started is to:`}</p>
    <p>{`1 create a new directory`}</p>
    <ol {...{
      "start": 2
    }}>
      <li parentName="ol">{`run `}<inlineCode parentName="li">{`npm init`}</inlineCode></li>
      <li parentName="ol">{`follow the prompts`}</li>
    </ol>
    <p>{`You'll get a basic `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "example-library",
  "version": "1.0.0",
  "description": "This is an example to show a basic package.json",
  "main": "src/index.js",
  "scripts": {
    "test": "echo \\"Error: no test specified\\" && exit 1"
  },
  "author": "Swizec",
  "license": "MIT"
}
`}</code></pre>
    <p>{`I recommend keeping your code in `}<inlineCode parentName="p">{`src/`}</inlineCode>{`. Export your public interface from `}<inlineCode parentName="p">{`src/index.js`}</inlineCode>{`.`}</p>
    <p>{`Internal libraries like to skip that step and it makes them hell to use. You want to hide implementation details.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// this leads to tears
import { FooUI } from "example-library/src/thingamabob/foo/UI";

// aim for this
import { FooUI } from "example-library";

// or this if there's many areas
import { FooUI } from "example-library/foo";
`}</code></pre>
    <p>{`Another good idea is to add a compile step. Get support for TypeScript, any Babel plugins you're used to, etc.`}</p>
    <p>{`I like to use `}<a parentName="p" {...{
        "href": "https://github.com/developit/microbundle"
      }}>{`microbundle`}</a>{` for libraries. Quick to set up, works great, keeps code small.`}</p>
    <h2 {...{
      "id": "how-to-maintain-a-private-library"
    }}>{`How to maintain a private library`}</h2>
    <p>{`You want to treat private libraries with the respect you'd give a public library.`}</p>
    <p>{`Follow `}<a parentName="p" {...{
        "href": "https://semver.org/"
      }}>{`semver`}</a>{` as best you can – major version for breaking changes, minor for new features, patch for bugfixes. You'll use different versions of the library in different projects. Upgrade as needed. Semver helps you keep track.`}</p>
    <p>{`You don't want a situation where `}<em parentName="p">{`every`}</em>{` project is forced to upgrade when `}<em parentName="p">{`any`}</em>{` project upgrades.`}</p>
    <p>{`Ask me how I know 😅`}</p>
    <ol>
      <li parentName="ol">{`Use `}<inlineCode parentName="li">{`npm version major/minor/patch`}</inlineCode>{` to release versions. It updates package.json and adds a new tag.`}</li>
      <li parentName="ol">{`Use `}<inlineCode parentName="li">{`git tag v1.1.0 v1.1.0\\^\\{\\} -f -m "changelog"`}</inlineCode>{` to add a message to your tag`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`git push origin --tags`}</inlineCode>{` to update your code`}</li>
    </ol>
    <p>{`Using tags for versions and the changelog creates a nice browing experience on GitHub. Tags show up as releases and you can see what each version means.`}</p>
    <p>{`Here's useAuth for example:`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/9ba5293cc42ee3fb225bd794ded82672/a3767/Example-changelog-from-useAuth6eg56f.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "32.28699551569507%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'130\\'%20viewBox=\\'0%200%20400%20130\\'%20preserveAspectRatio=\\'none\\'/%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9ba5293cc42ee3fb225bd794ded82672/ca0a1/Example-changelog-from-useAuth6eg56f.webp 223w", "/static/9ba5293cc42ee3fb225bd794ded82672/75680/Example-changelog-from-useAuth6eg56f.webp 445w", "/static/9ba5293cc42ee3fb225bd794ded82672/8d1ba/Example-changelog-from-useAuth6eg56f.webp 890w", "/static/9ba5293cc42ee3fb225bd794ded82672/efe91/Example-changelog-from-useAuth6eg56f.webp 1210w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9ba5293cc42ee3fb225bd794ded82672/e92b6/Example-changelog-from-useAuth6eg56f.png 223w", "/static/9ba5293cc42ee3fb225bd794ded82672/e66bf/Example-changelog-from-useAuth6eg56f.png 445w", "/static/9ba5293cc42ee3fb225bd794ded82672/4ef49/Example-changelog-from-useAuth6eg56f.png 890w", "/static/9ba5293cc42ee3fb225bd794ded82672/a3767/Example-changelog-from-useAuth6eg56f.png 1210w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/9ba5293cc42ee3fb225bd794ded82672/4ef49/Example-changelog-from-useAuth6eg56f.png",
                "alt": "Example changelog from useAuth",
                "title": "Example changelog from useAuth",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Example changelog from useAuth`}</figcaption>{`
  `}</figure></p>
    <p>{`Treat libraries as real projects. Have an owner. `}<em parentName="p">{`"I owned X library used by 5 products at `}{`\\`}{`\\`}{`$Company"`}</em>{` Looks great on your resumé `}{`[name|]`}{` 😉`}</p>
    <h2 {...{
      "id": "git-branch--the-poor-mans-private-package"
    }}>{`Git branch – the poor man's private package`}</h2>
    <p>{`Quickest way to distribute private packages is through GitHub. You're all set up already.`}</p>
    <p>{`Your company likely uses private GitHub repositories. That means your build/CI/deploy pipeline has access. 🤘`}</p>
    <p>{`Here's what you do:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm install company/repo
# or
yarn add company/repo
`}</code></pre>
    <p>{`You get a package.json entry like this: `}<inlineCode parentName="p">{`github:company/repo`}</inlineCode></p>
    <p>{`Yep! NPM and Yarn have built-in support for installing packages off GitHub repositories. Master branch by default, specify a version with a hash like this: `}<inlineCode parentName="p">{`github:company/repo#v1.0.0`}</inlineCode></p>
    <p>{`No support for automatic semver upgrades. Pinned versions only.`}</p>
    <h2 {...{
      "id": "private-npm-packages"
    }}>{`Private NPM packages`}</h2>
    <p>{`A step up from github branches are `}<a parentName="p" {...{
        "href": "https://docs.npmjs.com/about-private-packages"
      }}>{`private NPM packages`}</a>{`. You'll need a paid account to enable this option.`}</p>
    <p>{`NPM is the default package registry which makes this option easy to configure. No configuration for personal use.`}</p>
    <p>{`You'll need to scope your package in `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "@swizec/private-package"
}
`}</code></pre>
    <p>{`Now you can run `}<inlineCode parentName="p">{`npm publish`}</inlineCode>{` to publish as a private package. `}<inlineCode parentName="p">{`npm install @swizec/private-package`}</inlineCode>{` installs it.`}</p>
    <p>{`For team and collaborator access, you'll need to create a new token in settings. Anyone who needs access (person, server, etc) will use an `}<inlineCode parentName="p">{`NPM_TOKEN=...`}</inlineCode>{` environment var.`}</p>
    <p><img parentName="p" {...{
        "src": "/f8cef2e86b920a7c67ad8d15860d12a5/How-to-create-NPM-access-tokensh3hj56.gif",
        "alt": "How to create NPM access tokens"
      }}></img></p>
    <p>{`That's all you need ✌️`}</p>
    <h3 {...{
      "id": "private-npm-packages-with-yarn"
    }}>{`Private NPM packages with Yarn`}</h3>
    <p>{`I ran into issues using private NPM packages with Yarn. Errors out with packages it can't find.`}</p>
    <p>{`You can fix that with a `}<inlineCode parentName="p">{`.yarnrc`}</inlineCode>{` file in project root:`}</p>
    <pre><code parentName="pre" {...{}}>{`# .yarnrc
"@swizec:registry" "https://registry.npmjs.org/"
`}</code></pre>
    <p>{`You can commit this to git. Tells Yarn to look for `}<inlineCode parentName="p">{`@swizec`}</inlineCode>{`-scoped packages on the NPM registry.`}</p>
    <h3 {...{
      "id": "deploy-private-npm-packages-to-vercel-or-netlify"
    }}>{`Deploy private NPM packages to Vercel or Netlify`}</h3>
    <p><em parentName="p">{`Any`}</em>{` collaborator can get access to your private packages with a `}<inlineCode parentName="p">{`NPM_TOKEN=...`}</inlineCode>{` environment variable. That includes Vercel, Netlify, and other deploy environments.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/4b83ad82f638fe1fd478dc3fbfc8af4e/d3d45/NPM_TOKEN-in-Vercel-configdc0f4d.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "36.77130044843049%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'146\\'%20viewBox=\\'0%200%20400%20146\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M341%2049v18l20%201h19V48h-19l-20%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/4b83ad82f638fe1fd478dc3fbfc8af4e/ca0a1/NPM_TOKEN-in-Vercel-configdc0f4d.webp 223w", "/static/4b83ad82f638fe1fd478dc3fbfc8af4e/75680/NPM_TOKEN-in-Vercel-configdc0f4d.webp 445w", "/static/4b83ad82f638fe1fd478dc3fbfc8af4e/8d1ba/NPM_TOKEN-in-Vercel-configdc0f4d.webp 890w", "/static/4b83ad82f638fe1fd478dc3fbfc8af4e/3838e/NPM_TOKEN-in-Vercel-configdc0f4d.webp 1335w", "/static/4b83ad82f638fe1fd478dc3fbfc8af4e/79c03/NPM_TOKEN-in-Vercel-configdc0f4d.webp 1614w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/4b83ad82f638fe1fd478dc3fbfc8af4e/e92b6/NPM_TOKEN-in-Vercel-configdc0f4d.png 223w", "/static/4b83ad82f638fe1fd478dc3fbfc8af4e/e66bf/NPM_TOKEN-in-Vercel-configdc0f4d.png 445w", "/static/4b83ad82f638fe1fd478dc3fbfc8af4e/4ef49/NPM_TOKEN-in-Vercel-configdc0f4d.png 890w", "/static/4b83ad82f638fe1fd478dc3fbfc8af4e/4e814/NPM_TOKEN-in-Vercel-configdc0f4d.png 1335w", "/static/4b83ad82f638fe1fd478dc3fbfc8af4e/d3d45/NPM_TOKEN-in-Vercel-configdc0f4d.png 1614w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/4b83ad82f638fe1fd478dc3fbfc8af4e/4ef49/NPM_TOKEN-in-Vercel-configdc0f4d.png",
                "alt": "NPM_TOKEN in Vercel config",
                "title": "NPM_TOKEN in Vercel config",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`NPM_TOKEN in Vercel config`}</figcaption>{`
  `}</figure></p>
    <p>{`No other config necessary. Try making a new access token for each collaborator. They're free and the less reuse, the better.`}</p>
    <h2 {...{
      "id": "private-github-packages"
    }}>{`Private GitHub packages`}</h2>
    <p>{`Github now offers `}<a parentName="p" {...{
        "href": "https://docs.github.com/en/free-pro-team@latest/packages/publishing-and-managing-packages/about-github-packages"
      }}>{`private packages`}</a>{`. You get a few with a free account and you'll need a paid account for serious use.`}</p>
    <p>{`This is a great option because your company has a paid account. Right? Most do.`}</p>
    <p>{`Takes `}<a parentName="p" {...{
        "href": "https://docs.github.com/en/free-pro-team@latest/packages/using-github-packages-with-your-projects-ecosystem/configuring-npm-for-use-with-github-packages"
      }}>{`more configuration`}</a>{` than the NPM approach.`}</p>
    <p>{`You'll need to create a `}<a parentName="p" {...{
        "href": "https://docs.github.com/en/free-pro-team@latest/github/authenticating-to-github/creating-a-personal-access-token"
      }}>{`personal access token`}</a>{` with `}<a parentName="p" {...{
        "href": "https://docs.github.com/en/free-pro-team@latest/packages/publishing-and-managing-packages/about-github-packages#about-scopes-and-permissions-for-package-registries"
      }}>{`permission scopes for packages`}</a>{`.`}</p>
    <p>{`Then you add that token to your `}<inlineCode parentName="p">{`~/.npmrc`}</inlineCode>{` file. This is the global .npmrc file, `}<em parentName="p">{`not in the project`}</em>{`. Do not commit to git.`}</p>
    <pre><code parentName="pre" {...{}}>{`# ~/.npmrc

//npm.pkg.github.com/:_authToken=TOKEN
`}</code></pre>
    <p>{`Then you tell NPM to use the GitHub registry for your package scope. In a local `}<inlineCode parentName="p">{`.npmrc`}</inlineCode>{` file that's added to git.`}</p>
    <pre><code parentName="pre" {...{}}>{`# .npmrc
registry=https://registry.npmjs.org/
@swizec:registry=https://npm.pkg.github.com/
`}</code></pre>
    <p>{`For Yarn that would be:`}</p>
    <pre><code parentName="pre" {...{}}>{`# .yarnrc
"@swizec:registry" "https://registry.npmjs.org/"
`}</code></pre>
    <p>{`You have to ensure your `}<inlineCode parentName="p">{`repository`}</inlineCode>{` field in package.json matches your GitHub project:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "repository": "git://github.com/swizec/private-package.git"
}
`}</code></pre>
    <p><em parentName="p">{`And then`}</em>{`, you have to add a `}<inlineCode parentName="p">{`publishConfig`}</inlineCode>{` to your package.json.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "publishConfig": {
    "registry": "https://npm.pkg.github.com"
  }
}
`}</code></pre>
    <p>{`You can now publish with `}<inlineCode parentName="p">{`npm publish`}</inlineCode>{` and install with `}<inlineCode parentName="p">{`npm install @swizec/private-package`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "create-a-private-package-registry"
    }}>{`Create a private package registry`}</h2>
    <p>{`You can go super private with a custom package registry.`}</p>
    <p><a parentName="p" {...{
        "href": "https://verdaccio.org/en/"
      }}>{`Verdaccio`}</a>{` looks promising as an on-premise registry host that you can run yourself. Gives you full control and whatnot.`}</p>
    <p>{`I took one look at the Verdaccio docs, 3 looks at GitHub docs, and paid $7/mo for NPM. Ain't nobody got time for that.`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      